import React, { useCallback, useMemo, useState } from 'react'
import { rem } from 'polished'
import useOfferList from 'hooks/Offers/useOfferList'
import OfferCarouselHeader from './common/OfferCarouselHeader'
import OfferCarouselFilters from './common/OfferCarouselFilters'
import { MAX_CAROUSEL_ITEM_COUNT } from './common/constants'
import { LOOKING_FOR_SOMETHING_ELSE_CONTENT } from '../LookingForSomethingElse/constants'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import styled from 'styled-components'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import useHolidayTypes from 'home/hooks/useHolidayTypes'
import { ALL_HOTEL_TYPES, LE_CONTRACTED_HOTEL_TYPES } from 'constants/offer'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import qs from 'qs'
import { OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import { useInView } from 'react-intersection-observer'

const SectionContainer = styled(VerticalSpacer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
  overflow-x: hidden;
`

const DEFAULT_FILTERS = {
  offerTypes: LE_CONTRACTED_HOTEL_TYPES,
  frontPageVisibleOnly: true,
  limit: MAX_CAROUSEL_ITEM_COUNT,
}

interface Props {
  sortBy?: 'value';
  title?: React.ReactNode;
  shouldTriggerExperiemts?: boolean
}

function HotelOfferCarousel(props: Props) {
  const { sortBy, title, shouldTriggerExperiemts = false } = props
  const isManualSortingEnabled = isManualSortingEnabledFor('carousel-hotel')
  const [selectedFilter, setSelectedFilter] = useState<string>()

  // only do the AB test for not login AU users, will trigger the experiment when they view the carousel
  const { ref, inView } = useInView({ triggerOnce: true })
  useOptimizelyExperiment(OptimizelyServerSideExperiments.lereHomepageTopPickCarousel, shouldTriggerExperiemts && inView)

  const defaultListFilters: App.OfferListFilters = useMemo(() => {
    if (isManualSortingEnabled && !sortBy) {
      return {
        offerTypes: ALL_HOTEL_TYPES,
        sortBy: 'carousel-hotel',
        limit: MAX_CAROUSEL_ITEM_COUNT,
      }
    } else {
      return {
        ...DEFAULT_FILTERS,
        sortBy,
      }
    }
  }, [isManualSortingEnabled, sortBy])

  const offerListFilters: App.OfferListFilters = useMemo(() => {
    return {
      ...defaultListFilters,
      holidayTypes: selectedFilter ? [selectedFilter] : undefined,
    }
  }, [defaultListFilters, selectedFilter])

  const offerList = useOfferList(offerListFilters)

  const filters = useHolidayTypes(defaultListFilters, 5)

  const toggleFilter = useCallback((name: string) => {
    setSelectedFilter(selectedFilter === name ? undefined : name)
  }, [selectedFilter])

  const to = useMemo(() => {
    if (offerListFilters.holidayTypes || offerListFilters.sortBy === 'value') {
      const query = {
        holidayTypes: offerListFilters.holidayTypes,
        sortBy: offerListFilters.sortBy === 'value' ? 'value' : undefined,
      }
      return `/hotels?${qs.stringify(query, { arrayFormat: 'comma' })}`
    }
    return LOOKING_FOR_SOMETHING_ELSE_CONTENT.hotels.to
  }, [offerListFilters])

  if (offerList.offerIds.length === 0 && !selectedFilter && !offerList.fetching) {
    return null
  }

  const headingPrefix = sortBy ? 'Best value' : 'Limited-time'

  return (
    <SectionContainer gap={20}>
      <LayoutContainer ref={ref}>
        <VerticalSpacer gap={20}>
          <OfferCarouselHeader
            allTo="/hotels"
          >
            {title ?? <>{headingPrefix} <i>hotel </i>offers</>}
          </OfferCarouselHeader>
          {!shouldTriggerExperiemts && <OfferCarouselFilters
            options={filters}
            active={selectedFilter}
            onClick={toggleFilter}
          />}
        </VerticalSpacer>
      </LayoutContainer>
      <OfferListCarousel
        filters={offerListFilters}
        mobileLimit={8}
      >
        {!shouldTriggerExperiemts && <OfferCarouselLookingForSomethingElse to={to} />}
      </OfferListCarousel>
    </SectionContainer>
  )
}

export default React.memo(HotelOfferCarousel)
